/**
 * Web analytics kimlik yonetimi.
 * uid: first-party cookie (+ localStorage yedegi) ile kalici ziyaretci kimligi.
 * sid: sessionStorage ile oturum kimligi; inaktivite suresi asilirsa yenilenir.
 */
import log2 from "../../js/log2-lite";

const log = log2("TRACKER_ID");

const UID_KEY = "_supsis_uid";
const SID_KEY = "_supsis_sid";
const SID_TS_KEY = "_supsis_sid_ts";
const SID_SEEN_KEY = "_supsis_seen";
const UID_MAX_AGE_DAYS = 400;

export function uuid() {
    try {
        if (window.crypto && typeof window.crypto.randomUUID === "function") {
            return window.crypto.randomUUID();
        }
        if (window.crypto && typeof window.crypto.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            let hex = "";
            for (let i = 0; i < bytes.length; i++) {
                hex += (bytes[i] + 0x100).toString(16).slice(1);
            }
            return (
                hex.slice(0, 8) +
                "-" +
                hex.slice(8, 12) +
                "-" +
                hex.slice(12, 16) +
                "-" +
                hex.slice(16, 20) +
                "-" +
                hex.slice(20)
            );
        }
    } catch (e) {
        /* asagidaki Math.random yedegi kullanilir */
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** Cookie'yi ana domain'e yazar; alt domain'ler arasi kimlik korunur. */
function rootDomain() {
    const host = window.location.hostname;
    if (/^[0-9.]+$/.test(host) || host.indexOf(".") === -1) return null;
    const parts = host.split(".");
    // ornek: shop.acme.com.tr -> acme.com.tr, shop.acme.com -> acme.com
    const size = parts.length > 2 && parts[parts.length - 2].length <= 3 ? 3 : 2;
    return parts.slice(-size).join(".");
}

function readCookie(name) {
    try {
        const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
        return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
        return null;
    }
}

function writeCookie(name, value, days) {
    try {
        const expires = new Date(Date.now() + days * 86400000).toUTCString();
        const domain = rootDomain();
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie =
            name +
            "=" +
            encodeURIComponent(value) +
            "; expires=" +
            expires +
            "; path=/; SameSite=Lax" +
            (domain ? "; domain=." + domain : "") +
            secure;
    } catch (e) {
        log.debug("cookie write failed", e);
    }
}

function readStore(storage, key) {
    try {
        return window[storage].getItem(key);
    } catch (e) {
        return null;
    }
}

function writeStore(storage, key, value) {
    try {
        window[storage].setItem(key, value);
    } catch (e) {
        /* private mode: bellekteki deger yeterli */
    }
}

function removeStore(storage, key) {
    try {
        window[storage].removeItem(key);
    } catch (e) {
        /* yoksay */
    }
}

class Identity {
    constructor() {
        this.sessionTimeoutMs = 30 * 60 * 1000;
        this.uidValue = null;
        this.sidValue = null;
        this.isNewVisitor = false;
        this.isNewSession = false;
    }

    configure(sessionTimeoutMinutes) {
        const minutes = parseInt(sessionTimeoutMinutes, 10);
        if (minutes > 0) this.sessionTimeoutMs = minutes * 60 * 1000;
    }

    /** Kalici ziyaretci kimligi; cookie yoksa localStorage'dan kurtarilir. */
    uid() {
        if (this.uidValue) return this.uidValue;
        let value = readCookie(UID_KEY) || readStore("localStorage", UID_KEY);
        if (!value) {
            value = uuid();
            this.isNewVisitor = true;
        }
        writeCookie(UID_KEY, value, UID_MAX_AGE_DAYS);
        writeStore("localStorage", UID_KEY, value);
        this.uidValue = value;
        return value;
    }

    /** Oturum kimligi; son aktiviteden sessionTimeout kadar sure gectiyse yenilenir. */
    sid() {
        const now = Date.now();
        const stored = readStore("sessionStorage", SID_KEY);
        const storedTs = parseInt(readStore("sessionStorage", SID_TS_KEY), 10) || 0;
        const expired = !storedTs || now - storedTs > this.sessionTimeoutMs;

        if (stored && !expired) {
            this.sidValue = stored;
            writeStore("sessionStorage", SID_TS_KEY, String(now));
            return stored;
        }

        const value = uuid();
        this.sidValue = value;
        this.isNewSession = true;
        writeStore("sessionStorage", SID_KEY, value);
        writeStore("sessionStorage", SID_TS_KEY, String(now));
        return value;
    }

    /** Her event'te cagrilir; oturumun zaman asimina dusmesini engeller. */
    touch() {
        writeStore("sessionStorage", SID_TS_KEY, String(Date.now()));
    }

    /** Bu tarayicida daha once ziyaret var mi (session_start icin). */
    isReturning() {
        const seen = readStore("localStorage", SID_SEEN_KEY);
        if (!seen) {
            writeStore("localStorage", SID_SEEN_KEY, String(Date.now()));
            return false;
        }
        return true;
    }

    /** SDK reset komutu: kimlikleri sifirlar, sonraki event yeni ziyaretci uretir. */
    reset() {
        this.uidValue = null;
        this.sidValue = null;
        writeCookie(UID_KEY, "", -1);
        removeStore("localStorage", UID_KEY);
        removeStore("sessionStorage", SID_KEY);
        removeStore("sessionStorage", SID_TS_KEY);
        log.debug("identity reset");
    }
}

export const identity = new Identity();
export default identity;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/identity.js
// module id = null
// module chunks = 